<template>
  <div class="lj-report-list-wrapper">
    <CRow>
      <CCol>
        <CCard class="main-card">
          <CCardHeader>
            <div class="d-flex justify-content-between align-items-center flex-wrap">
              <div>
                <h5 class="mb-0">
                  <i class="fas fa-file-alt me-2"></i>
                  <strong>LJ Reports</strong>
                </h5>
                <small class="text-muted">
                  Pick a stock to view or print its LJ Report
                </small>
              </div>
              <CBadge color="info">
                {{ totalItems }} {{ totalItems === 1 ? 'Stock' : 'Stocks' }}
              </CBadge>
            </div>
          </CCardHeader>

          <CCardBody>
            <!-- Filter row -->
            <CRow class="mb-3">
              <CCol md="3">
                <CInput
                  label="Stock No"
                  v-model="filters.stockNo"
                  @input="onFilterChange"
                  placeholder="Search stock no"
                />
              </CCol>
              <CCol md="3">
                <CInput
                  label="Customer"
                  v-model="filters.customerName"
                  @input="onFilterChange"
                  placeholder="Customer name"
                />
              </CCol>
              <CCol md="3">
                <CInput
                  label="Salesman"
                  v-model="filters.salesmanName"
                  @input="onFilterChange"
                  placeholder="Salesman name"
                />
              </CCol>
              <CCol md="3">
                <CInput
                  label="Loan Bank"
                  v-model="filters.loanBankName"
                  @input="onFilterChange"
                  placeholder="Bank name"
                />
              </CCol>
            </CRow>

            <div v-if="loading" class="text-center py-5">
              <CSpinner color="primary" />
              <p class="text-muted mt-3">Loading...</p>
            </div>

            <CDataTable
              v-else
              :items="items"
              :fields="fields"
              hover
              striped
              :noItemsView="{ noItems: 'No stocks with sales found' }"
            >
              <template #show_index="{ index }">
                <td class="text-center text-muted">
                  {{ (page - 1) * itemsPerPage + index + 1 }}
                </td>
              </template>

              <template #stockNo="{ item }">
                <td><strong>{{ item.stockNo }}</strong></td>
              </template>

              <template #customerName="{ item }">
                <td>{{ item.customerName || '—' }}</td>
              </template>

              <template #salesmanName="{ item }">
                <td>{{ item.salesmanName || '—' }}</td>
              </template>

              <template #vehicleRegistrationDate="{ item }">
                <td>{{ formatDate(item.vehicleRegistrationDate) }}</td>
              </template>

              <template #vehicleRegistrationNumber="{ item }">
                <td>{{ item.vehicleRegistrationNumber || '—' }}</td>
              </template>

              <template #totalPriceOtr="{ item }">
                <td class="text-right">RM {{ formatMoney(item.totalPriceOtr) }}</td>
              </template>

              <template #loanBankName="{ item }">
                <td>{{ item.loanBankName || '—' }}</td>
              </template>

              <template #actions="{ item }">
                <td class="text-right">
                  <CButton
                    size="sm"
                    color="primary"
                    @click="openReport(item)"
                  >
                    <i class="fas fa-file-alt me-1"></i>
                    View LJ Report
                  </CButton>
                </td>
              </template>
            </CDataTable>

            <CPagination
              v-if="totalPages > 1"
              :pages="totalPages"
              :active-page.sync="page"
              align="center"
            />
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>
  </div>
</template>

<script>
import LjReportApi from "../../lib/ljReportApi";
import { formatMalaysiaDate } from "@/utils/dateUtil";

export default {
  name: "LjReportList",
  data() {
    return {
      api: new LjReportApi(),
      loading: false,
      items: [],
      totalItems: 0,
      page: 1,
      itemsPerPage: 20,
      filters: {
        stockNo: "",
        customerName: "",
        salesmanName: "",
        loanBankName: "",
      },
      _filterDebounce: null,
      fields: [
        { key: "show_index", label: "#", _style: "width: 50px" },
        { key: "stockNo", label: "Stock No" },
        { key: "customerName", label: "Customer" },
        { key: "salesmanName", label: "Salesman" },
        { key: "vehicleRegistrationDate", label: "Reg Date" },
        { key: "vehicleRegistrationNumber", label: "Reg Number" },
        { key: "totalPriceOtr", label: "Total Price OTR", _classes: "text-right" },
        { key: "loanBankName", label: "Loan Bank" },
        { key: "actions", label: "", _style: "width: 180px" },
      ],
    };
  },
  computed: {
    totalPages() {
      return Math.max(1, Math.ceil(this.totalItems / this.itemsPerPage));
    },
  },
  watch: {
    page() {
      this.load();
    },
  },
  created() {
    this.load();
  },
  methods: {
    load() {
      const self = this;
      self.loading = true;
      const params = {
        page: self.page,
        itemsPerPage: self.itemsPerPage,
        filters: {
          stockNo: self.filters.stockNo,
          customerName: self.filters.customerName,
          salesmanName: self.filters.salesmanName,
          loanBankName: self.filters.loanBankName,
        },
      };
      self.api
        .getList(params)
        .then((data) => {
          self.items = data.items || [];
          self.totalItems = data.totalItems || 0;
        })
        .catch(() => {
          self.items = [];
          self.totalItems = 0;
        })
        .finally(() => {
          self.loading = false;
        });
    },
    onFilterChange() {
      // Debounce while typing.
      clearTimeout(this._filterDebounce);
      this._filterDebounce = setTimeout(() => {
        this.page = 1;
        this.load();
      }, 350);
    },
    openReport(item) {
      this.$router.push({ name: "ljReportById", params: { id: item.stockId } });
    },
    formatDate(d) {
      return d ? formatMalaysiaDate(d) : "—";
    },
    formatMoney(n) {
      const v = Number(n) || 0;
      return v.toLocaleString("en-MY", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
  },
};
</script>

<style scoped>
.lj-report-list-wrapper {
  padding: 1rem;
}
</style>
